// src/components/admin/SisterServicesStrip.jsx
// --------------------------------------------
// db-205 "Sister services" strip on the DFV Service record (ShelterDetail), just
// above the tabs — so it shows in BOTH Manage → DFV Services and the telemarketing
// call screen (same shared editor). SELF-LOADING from the shelter id, so
// ShelterDetail needs no new props (no "wire it in two places" — CLAUDE.md).
//
// FAIL-SAFE by design: any load error / null result renders NOTHING, so the
// record screen can never break because of this strip.
// Chips are NOT clickable (David, 4 Oct 2026) — hover shows the service name.

import React from "react";
import { listAllShelters } from "../../data/shelters";
import { listActiveSisterGroups, listActiveSisterMembers } from "../../data/sisterGroups";
import { buildSisterIndex } from "../../services/sisterGroups";
import { SisterGroupModal } from "./SisterGroupModal.jsx";

export function SisterServicesStrip({ shelterId, shelterCode }) {
  // null = loading or failed (render nothing); otherwise { entry, groups }.
  const [state, setState] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    if (!shelterId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [members, groups, shelters] = await Promise.all([
          listActiveSisterMembers(),
          listActiveSisterGroups(),
          listAllShelters(),
        ]);
        if (cancelled) return;
        if (!members || !groups || !shelters) {
          setState(null);
          return;
        }
        const entry = buildSisterIndex(members, groups, shelters).get(shelterId) ?? null;
        setState({ entry, groups });
      } catch {
        if (!cancelled) setState(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shelterId, reloadKey]);

  if (!shelterId || !state) return null;
  const { entry, groups } = state;

  const modal = editing ? (
    <SisterGroupModal
      shelterId={shelterId}
      shelterCode={shelterCode}
      entry={entry}
      groups={groups}
      onClose={() => setEditing(false)}
      onSaved={() => {
        setEditing(false);
        setReloadKey((k) => k + 1);
      }}
    />
  ) : null;

  if (!entry) {
    return (
      <div className="px-5 pt-2">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-[12px] text-zephyr-teal-dark underline bg-transparent border-none p-0 cursor-pointer"
        >
          Add to sister group
        </button>
        {modal}
      </div>
    );
  }

  return (
    <div className="mx-4 my-3 flex items-center gap-[10px] flex-wrap bg-zephyr-teal-light border border-[#9fd3cc] rounded-[10px] py-[8px] px-[14px]">
      {entry.sisters.length > 0 ? (
        <>
          <span className="text-[13px] font-bold text-zephyr-teal-dark">Sister services:</span>
          {entry.sisters.map((s) => (
            <span
              key={s.id}
              title={s.name}
              className="inline-flex items-center bg-white border border-[#80CBC4] text-zephyr-teal-dark rounded-full py-[2px] px-[10px] text-[12px] font-semibold cursor-default"
            >
              {s.code}
            </span>
          ))}
          <span className="ml-auto text-[12px] text-zephyr-muted">
            Group: <b>{entry.groupName}</b>
          </span>
        </>
      ) : (
        <span className="text-[13px] text-zephyr-teal-dark">
          Sister group: <b>{entry.groupName}</b> — no other services yet
        </span>
      )}
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={`${entry.sisters.length > 0 ? "" : "ml-auto "}rounded-[7px] py-[5px] px-3 text-[12px] font-semibold bg-white text-zephyr-teal-dark border border-[#80CBC4] cursor-pointer`}
      >
        Edit sister group
      </button>
      {modal}
    </div>
  );
}
